/**
 * ENG-6441: the host-side connectivity-probe execution context, extracted from
 * manager-worker.ts so BOTH the manager's periodic probe loop AND the on-demand
 * `agt integration probe` command build the SAME probeEnv + probeDeps — there is
 * exactly one definition of "how a probe runs on this host", so the two can't
 * drift (a managed-Composio false-RED fixed in one would silently persist in the
 * other).
 *
 * `executeConnectivityProbe` needs host-only inputs the central API does not have:
 * the agent's project `.mcp.json` (the wired server's url + headers — the exact
 * path the agent's tool calls take) and its `.env.integrations` (the per-agent
 * tokens that resolve templated auth like `Bearer ${GRANOLA_ACCESS_TOKEN}`). This
 * module assembles them; the manager and the CLI command both consume it. This is
 * also why `POST /integrations/:id/test` can only echo the last host-recorded
 * status — none of this context exists centrally.
 */
import { delimiter as pathDelimiter, join } from 'node:path';
import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import {
  probeComposioAccount,
  probeComposioMcpToolCall,
  resolveConnectivityProbe,
  REMOTE_MCP_PROXY_ENV,
  buildForwardHeaders,
  readRemoteMcpAuthConfig,
  isDefaultRemoteMcpConnection,
  remoteMcpServerKey,
  PROBE_TOOLKIT_PATH_DIRS,
  PROBE_HOME_RELATIVE_PATH_SEGMENTS,
  PROBE_SYSTEM_PATH_FLOOR,
} from '@augmented/core/integrations';
import type { ConnectivityTestOverride, ToolkitSourceType } from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';
import type { ConnectivityProbeDeps } from './connectivity-probe-executor.js';
import { probeMcpHttp } from './mcp-probe-client.js';
import { probeMcpStdio } from './mcp-stdio-probe.js';
import { parseEnvIntegrations, expandTemplateVars, LATE_BOUND_VARS } from './mcp-env-probe.js';
import {
  inlineEntryLookup,
  verdictForUnavailableMcpConfig,
  type McpConfigLookup,
} from './mcp-config-lookup.js';
import { runCliProbe } from './cli-probe.js';
import { resolveIntegrationServerKeys, sanitizeServerKey } from './session-tool-bind-runner.js';

/**
 * ENG-8049: a raw `.mcp.json` server entry (the object stored under
 * `mcpServers[key]`). Shared by the file-read path and the inline path so a
 * quarantined integration - absent from `.mcp.json` - can be probed from a
 * server entry the manager reconstructs via the framework adapter.
 */
export interface McpJsonServerEntry {
  type?: string;
  url?: string;
  headers?: Record<string, string>;
  command?: string;
  args?: string[];
  env?: Record<string, string>;
}

// ENG-6232: substitute `${VAR}` placeholders in the url + header values against
// the probe env (the agent's `.env.integrations` overlaid onto the manager env).
// The .mcp.json carries templated auth like `Authorization: Bearer ${GRANOLA_ACCESS_TOKEN}`;
// Claude Code expands it from the agent's spawn env, but the manager process
// running this probe does not carry per-agent tokens. Without substitution the
// probe sends the literal `${VAR}` → guaranteed 401 → false `down`. `unresolved`
// names any var we could not substitute so the caller can skip rather than fire
// a doomed request. ENG-8049: extracted from readMcpHttpServerConfig so the
// inline-entry path resolves identically.
export function resolveHttpServerEntry(
  entry: McpJsonServerEntry,
  env?: NodeJS.ProcessEnv,
): { url: string; headers?: Record<string, string>; unresolved: string[] } | null {
  const unresolved = new Set<string>();
  const sub = (value: string): string => {
    if (!env) return value;
    const r = expandTemplateVars(value, env);
    for (const name of r.unresolved) unresolved.add(name);
    return r.value;
  };

  if (typeof entry.url === 'string' && (entry.type === 'http' || entry.type === undefined)) {
    const url = sub(entry.url);
    let headers: Record<string, string> | undefined;
    if (entry.headers) {
      headers = {};
      for (const [k, v] of Object.entries(entry.headers)) headers[k] = sub(v);
    }
    return { url, ...(headers ? { headers } : {}), unresolved: [...unresolved] };
  }

  // ENG-6859: the remote OAuth integrations (brand-ninja, granola) are now
  // wired as the stdio remote-oauth-proxy rather than a direct http entry, so
  // reconstruct the equivalent {url, headers} the probe needs from the proxy's
  // env. The proxy forwards to AGT_REMOTE_MCP_URL with the credential read live
  // from AGT_REMOTE_MCP_TOKEN_VAR (resolved here from the overlaid
  // .env.integrations, exactly like the old templated header). An unresolved
  // token var lands in `unresolved` so the caller skips rather than false-downs.
  //
  // ENG-8357: this used to hardcode `Authorization: Bearer <token>` while the
  // proxy had grown AGT_REMOTE_MCP_AUTH_HEADER + AGT_REMOTE_MCP_EXTRA_HEADERS
  // (ENG-7748). For a non-Bearer remote the agent authenticated correctly and
  // the probe got a 401 — and a 401 at `initialize` is a REAL failure
  // observation, so it records `down`, which escalates to a hard-down /
  // needs-reauth on a WORKING integration within ~3 cycles. Both sides now build
  // headers from the same core helpers, so neither can grow an auth input the
  // other ignores.
  const proxyUrl = entry.env?.[REMOTE_MCP_PROXY_ENV.url];
  const tokenVar = entry.env?.[REMOTE_MCP_PROXY_ENV.tokenVar];
  if (entry.command && typeof proxyUrl === 'string' && typeof tokenVar === 'string') {
    const url = sub(proxyUrl);
    const token = sub(`\${${tokenVar}}`);
    const { authHeader, extras } = readRemoteMcpAuthConfig(entry.env);
    // Extra-header vars are read DIRECTLY from the probe env, not through
    // `sub()`, and an unreadable one ships empty rather than joining
    // `unresolved`. That mirrors the proxy exactly (empty ⇒ "no session bound"),
    // and it is load-bearing: Anchor's ANCHOR_BROWSER_SESSION_ID is in
    // LATE_BOUND_VARS, which expandTemplateVars marks unresolved
    // unconditionally — routing extras through `sub()` would make every such
    // entry skip forever, trading the false `down` for the perpetual false
    // `transient_error` of ENG-6428 / ENG-8205.
    const headers = buildForwardHeaders(token, authHeader, extras, (varName) => {
      const value = env?.[varName];
      return typeof value === 'string' ? value : null;
    });
    return { url, headers, unresolved: [...unresolved] };
  }

  return null;
}

// ENG-5641: read a streamable-HTTP MCP server's url + headers from the agent's
// project .mcp.json, for the connectivity probe.
//
// ENG-8363: returns a discriminated {@link McpConfigLookup} instead of a bare
// null. That null collapsed three different facts — no file, no key, wrong shape
// — and each probe leg then guessed what it meant (the HTTP leg guessed
// `transient_error`, the stdio leg guessed `down`, for identical input). The
// cause now reaches the caller so `verdictForUnavailableMcpConfig` decides once,
// for both.
export function readMcpHttpServerConfig(
  projectDir: string,
  serverKey: string,
  env?: NodeJS.ProcessEnv,
): McpConfigLookup<{ url: string; headers?: Record<string, string>; unresolved: string[] }> {
  let servers: Record<string, McpJsonServerEntry>;
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    servers = (JSON.parse(raw) as {
      mcpServers?: Record<string, McpJsonServerEntry>;
    }).mcpServers ?? {};
  } catch {
    return { ok: false, cause: 'file-unreadable' };
  }
  const entry = servers[serverKey];
  if (!entry) return { ok: false, cause: 'key-absent' };
  const value = resolveHttpServerEntry(entry, env);
  return value ? { ok: true, value } : { ok: false, cause: 'wrong-shape' };
}

/**
 * ENG-8345: pick the `.mcp.json` server key that ACTUALLY backs an integration,
 * by intersecting deterministic candidates against the keys the file really
 * declares — rather than trusting a DERIVED key to match what the writer wrote.
 *
 * `deriveMcpServerKey` sanitises the definition_id (`brand-ninja` →
 * `brand_ninja`), but the remote-MCP writer keys its entries by the RAW
 * definition_id (`mcpServers['brand-ninja']`, claudecode/index.ts). For any
 * hyphenated remote definition the derived key therefore names a server that
 * does not exist, the lookup returns null, and the probe reports a
 * non-escalating `transient_error` — every cycle, forever. Because
 * `transient_error` never escalates to `down` (hysteresis needs a `down`
 * OBSERVATION) and the streak only resets on an `ok`, that grew an unbounded
 * FALSE streak: brand-ninja reached 433 and tripped the CRITICAL transient
 * spike alert, the same shape as the origami 339 (ENG-7405) and broker 280/282
 * (ENG-6428/ENG-8205) incidents before it.
 *
 * The durable fix is the invariant "a probe never INVENTS a server key" —
 * resolve it from what is declared. Reuses `resolveIntegrationServerKeys`, the
 * same candidate intersection the session-tool-bind probe already runs against
 * this very file, so a future writer branch cannot silently desync one probe
 * while the other keeps working.
 *
 * The derived key is passed as the HINT and therefore wins whenever it is
 * genuinely declared — Composio (`composio_outlook`) and every already-agreeing
 * case keep their exact current behaviour. Returns null when the file is
 * unreadable or nothing matches; callers fall back to the derived key so the
 * not-resolvable diagnostics are unchanged.
 */
export function resolveDeclaredServerKey(
  projectDir: string,
  definitionId: string,
  derivedKey: string,
): string | null {
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    const servers = (JSON.parse(raw) as {
      mcpServers?: Record<string, McpJsonServerEntry>;
    }).mcpServers ?? {};
    const declared = Object.keys(servers);

    // A NAMED connection (ENG-7543) derives `<base>-<connection_key>`, which is
    // neither bare form. For one of those an EXACT match is the only safe
    // answer: the candidate fallback would otherwise match the bare key — i.e.
    // probe the DEFAULT connection's server and report that verdict as the
    // named connection's health. That is a false GREEN, strictly worse than the
    // false alarm this function exists to remove, because an `ok` zeroes the
    // streak and closes any open alert.
    //
    // ENG-8359 made the exact match satisfiable: the writer now emits a
    // per-connection key (`<sanitized-base>-<connection_key>`) built from the
    // same shared rule this probe derives, so a live named connection resolves
    // here. Requiring the exact match is therefore the PERMANENT contract
    // between writer and probe, not a placeholder — a null now means the named
    // connection genuinely is not on disk, which is the honest answer.
    // (Before that change the writer keyed every entry `mcpServers[definition_id]`
    // bare, so two named connections clobbered each other and no suffixed key
    // was ever written.)
    if (derivedKey !== definitionId && derivedKey !== sanitizeServerKey(definitionId)) {
      return declared.includes(derivedKey) ? derivedKey : null;
    }

    return resolveIntegrationServerKeys(definitionId, declared, derivedKey)[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * ENG-7405: read a LOCAL STDIO MCP server's spawn config (command/args/env)
 * from the agent's `.mcp.json`, `${VAR}`-substituted from the probe env — the
 * stdio sibling of {@link readMcpHttpServerConfig}. Returns null when the key
 * isn't a stdio (command-bearing) entry or the file/key isn't resolvable.
 * `unresolved` names any `${VAR}` we couldn't substitute so the caller can skip
 * rather than spawn a server with a literal `${VAR}` in its env.
 */
// ENG-8049: extracted from readMcpStdioServerConfig so the inline-entry path
// resolves identically. Given a raw stdio server entry, `${VAR}`-substitute its
// args + env from the probe env, returning null when it isn't a command-bearing
// (stdio) entry.
export function resolveStdioServerEntry(
  entry: McpJsonServerEntry,
  env?: NodeJS.ProcessEnv,
): { command: string; args: string[]; env: Record<string, string>; unresolved: string[] } | null {
  if (typeof entry.command !== 'string') return null;

  // Only NON-late-bound unresolved vars block the probe. A late-bound var
  // (AGT_TOKEN et al.) unresolved in the manager's probe context is the
  // DESIGNED state, not a fault - see the late-bound handling for the env below.
  const unresolved = new Set<string>();
  const sub = (value: string): string => {
    if (!env) return value;
    const r = expandTemplateVars(value, env);
    for (const name of r.unresolved) if (!LATE_BOUND_VARS.has(name)) unresolved.add(name);
    return r.value;
  };

  // ENG-7405 follow-up: a spawn-env value whose ONLY unresolved refs are
  // late-bound (e.g. `AGT_TOKEN=${AGT_TOKEN}`) must NOT skip the probe. Those
  // vars are bound by a lower layer at real spawn time, or the server resolves
  // them itself - origami's broker identity exchanges AGT_API_KEY → /host/exchange
  // when AGT_TOKEN is absent (packages/origami-mcp-server identity.ts). Passing
  // the literal `${AGT_TOKEN}` would poison the child; treating it as a hard
  // unresolved var skips the probe every cycle, and since a non-'ok' verdict
  // increments consecutive_connectivity_failures, that yielded a false
  // `transient_error` streak that never cleared (the 339-count on every origami
  // agent). So OMIT the key - the child sees it unset and takes its designed
  // fallback, exactly as the live session does. A NON-late-bound unresolved var
  // is still a real gap and blocks (surfaced in `unresolved`).
  const resolvedEnv: Record<string, string> = {};
  for (const [k, v] of Object.entries(entry.env ?? {})) {
    if (!env) {
      resolvedEnv[k] = v;
      continue;
    }
    const r = expandTemplateVars(v, env);
    const hardUnresolved = r.unresolved.filter((name) => !LATE_BOUND_VARS.has(name));
    for (const name of hardUnresolved) unresolved.add(name);
    // Late-bound-only unresolved → drop the key (leave it unset for the child).
    if (hardUnresolved.length === 0 && r.unresolved.length > 0) continue;
    resolvedEnv[k] = r.value;
  }
  return {
    command: entry.command,
    args: (entry.args ?? []).map(sub),
    env: resolvedEnv,
    unresolved: [...unresolved],
  };
}

// ENG-8363: same discriminated result as the HTTP reader — see there for why the
// bare null had to go. Keeping the two readers structurally identical is the
// point: the verdict asymmetry this fixes grew out of two lookups that LOOKED
// alike but reported differently.
export function readMcpStdioServerConfig(
  projectDir: string,
  serverKey: string,
  env?: NodeJS.ProcessEnv,
): McpConfigLookup<{ command: string; args: string[]; env: Record<string, string>; unresolved: string[] }> {
  let servers: Record<string, McpJsonServerEntry>;
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    servers = (JSON.parse(raw) as {
      mcpServers?: Record<string, McpJsonServerEntry>;
    }).mcpServers ?? {};
  } catch {
    return { ok: false, cause: 'file-unreadable' };
  }
  const entry = servers[serverKey];
  if (!entry) return { ok: false, cause: 'key-absent' };
  const value = resolveStdioServerEntry(entry, env);
  return value ? { ok: true, value } : { ok: false, cause: 'wrong-shape' };
}

/**
 * ENG-6396: derive the `.mcp.json` server key for an integration's connectivity
 * probe — or `undefined` when the integration is not MCP-`tools/list`-probeable.
 *
 * The key is needed for any integration whose RESOLVED probe kind is an MCP
 * `tools/list` handshake against the agent's wired server: `managed_composite`
 * (Composio) AND `mcp_tools_list` (remote streamable-HTTP OAuth providers like
 * granola). The historical bug (the granola/Dwight incident) was keying off the
 * raw `source_type` string (`managed`/`mcp_server`) — granola's toolkit carries
 * `source_type: 'native'` despite being a remote MCP, so it got no key, its host
 * probe never ran, and it sat "Connected" with a dead token. Keying off the
 * resolver's kind (which already owns the `mcpUrl` detection) fixes every
 * remote-MCP provider, not just granola, and keeps the predicate in one place.
 *
 * Key derivation matches the managed-toolkit .mcp.json writer: definition_id
 * sanitised (non-alphanumeric → '_', lowercased) — e.g. 'composio_outlook',
 * 'granola'. An unresolvable key degrades to transient_error, never a false 'down'.
 */
export function deriveMcpServerKey(input: {
  definitionId: string;
  sourceType: ToolkitSourceType | null;
  authType: IntegrationAuthType | null;
  connectivityTest?: ConnectivityTestOverride | null;
  /**
   * ENG-7543: the connection this row is (default | slug). A named connection's
   * `.mcp.json` server is written under `<base>-<connection_key>` (Lane B's
   * mcp_server_key), so the probe must target the SAME suffixed key or it
   * handshakes the wrong (default) server and false-fails the named connection.
   */
  connectionKey?: string | null;
  /**
   * ENG-7077 — the catalog `remote_mcp.url`. This function decides the server key
   * FROM the resolver's kind, so it needs exactly what the resolver needs: omit
   * this for a static-token hosted remote MCP and the kind comes back `builtin`,
   * no key is derived, and the probe falls back to the bare definition_id.
   */
  remoteMcpUrl?: string | null;
}): string | undefined {
  const kind = resolveConnectivityProbe({
    definitionId: input.definitionId,
    sourceType: input.sourceType,
    authType: input.authType,
    connectivityTest: input.connectivityTest ?? null,
    remoteMcpUrl: input.remoteMcpUrl ?? null,
  }).kind;
  if (kind !== 'mcp_tools_list' && kind !== 'managed_composite') return undefined;
  const base = input.definitionId.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  // Default/absent → the sanitized base. Deliberately NOT the raw definition_id
  // even though the remote-MCP writer keys the default connection that way:
  // ENG-8345's resolveDeclaredServerKey intersects {raw, sanitized, hint}
  // against the keys actually declared, which bridges the two forms for the
  // default connection AND keeps this byte-identical for the managed lane.
  if (isDefaultRemoteMcpConnection(input.connectionKey)) return base;
  // ENG-8359: a NAMED connection's key comes from the WRITER's own rule, not a
  // local copy of it. resolveDeclaredServerKey requires an EXACT declared match
  // for a suffixed key (falling back to the bare key would probe the default
  // connection and report it as the named one's health — a false green), so if
  // this derivation ever drifted from what provisioning wrote, the named
  // connection would be permanently unprobeable with no error anywhere.
  return remoteMcpServerKey(input.definitionId, input.connectionKey);
}

/**
 * ENG-6206: overlay the agent's `.env.integrations` onto the manager's env so
 * CLI probes that assert auth (e.g. `gh auth status`) see the integration token
 * (GH_TOKEN/GITHUB_TOKEN) — it lives in the agent's env file, never the manager's
 * process env. Without this the gh probe would always report not-authenticated
 * (a false 'down'). Best-effort: a missing/unreadable file just falls back to the
 * manager env (the prior --version behaviour).
 *
 * ENG-9122: pass `agentId` so the probe child also carries `AGT_AGENT_ID`. The
 * ENG-8344 credential shim needs it to ask the broker for a token, and the
 * manager's own env has no such value to inherit.
 */
/**
 * ENG-9005's floor, and ENG-9284's toolkit dirs, now both imported from
 * `@augmented/core/integrations` rather than declared here.
 *
 * They moved because this file's copy was only HALF the answer and the other
 * half lived in two other files, in two languages. See `probe-path.ts` for the
 * full account; the short version is that `SYSTEM_PATH_FLOOR` never contained a
 * Homebrew dir, which on this fleet is the only place the real `gh` exists.
 */
const SYSTEM_PATH_FLOOR = PROBE_SYSTEM_PATH_FLOOR;

/**
 * The toolkit dirs a probe puts AHEAD of the inherited PATH (position 1),
 * resolved for THIS process (so `~/.local/bin` becomes a real path).
 * Existence is checked by the caller, not here.
 *
 * Exported for `connectivity-probe-context.test.ts`, which has to know which
 * entries `buildProbeEnv` is expected to move in order to assert that
 * everything else keeps its order. Re-deriving the list there would be a second
 * definition of "which dirs get precedence" — the exact drift this file spends
 * two long comments warning about — and it would have to duplicate the
 * `process.env.HOME?.trim() || homedir()` resolution to boot.
 */
export function toolkitPathDirs(): string[] {
  const home = process.env.HOME?.trim() || homedir();
  return [
    ...PROBE_TOOLKIT_PATH_DIRS,
    ...PROBE_HOME_RELATIVE_PATH_SEGMENTS.map((seg) => join(home, seg)),
  ];
}

export function buildProbeEnv(projectDir: string, agentId?: string | null): NodeJS.ProcessEnv {
  const probeEnv: NodeJS.ProcessEnv = { ...process.env };

  // ENG-9122 AC3 — stamp AGT_AGENT_ID, which is the last piece the shim needs
  // and the only one nothing was supplying.
  //
  // The two overlays below fix WHICH `gh` runs and WHICH token file it can read.
  // Neither helps if the shim cannot ask the broker who it is acting for: the
  // ENG-8344 credential path reads `process.env.AGT_AGENT_ID` and refuses
  // outright without it. The manager process legitimately does not have one -
  // it serves every agent on the host - so the variable is absent from
  // `process.env` here by construction, not by accident. `persistent-session.ts`
  // already stamps it per-agent for the SESSION (REQUIRED_SPAWN_ENV); the probe
  // lane is the sibling that was never given the same treatment.
  //
  // Observed cost: gordon/github probing as
  //   "gh probe inconclusive — our credential broker could not supply a token:
  //    augmented: GitHub credential fetch failed: AGT_AGENT_ID is not set in
  //    this process environment"
  // once an hour, each one counted as a GitHub failure, for a streak of 87.
  //
  try {
    const envIntPath = join(projectDir, '.env.integrations');
    if (existsSync(envIntPath)) {
      Object.assign(probeEnv, parseEnvIntegrations(readFileSync(envIntPath, 'utf-8')));
    }
  } catch {
    /* a probe must never break on a malformed env file */
  }

  // Stamped AFTER the .env.integrations overlay, deliberately (CodeRabbit).
  //
  // The caller passes the agent id it resolved from the agent row it is
  // probing. `.env.integrations` is a file on the host that may carry a stale
  // or simply wrong `AGT_AGENT_ID` - it is written per-agent, but nothing
  // enforces that, and a copied or leftover file would silently make the probe
  // mint a token for the WRONG AGENT. Overlaying the file last let it win.
  //
  // That is not a cosmetic precedence question. This whole ticket is about a
  // probe that reported a verdict it had not earned; a probe authenticating as
  // another agent would do the same thing with a credential attached. The
  // explicit, caller-validated value wins.
  //
  // Optional and blank-guarded on purpose: an empty string fails the shim's
  // `if (!AGT_AGENT_ID)` check exactly like an unset var, just less visibly
  // (persistent-session.ts makes the same point), and a caller with no agent id
  // to hand must leave whatever the file or the environment supplied alone
  // rather than clobber it with "".
  if (typeof agentId === 'string' && agentId.trim().length > 0) {
    probeEnv.AGT_AGENT_ID = agentId.trim();
  }

  // ENG-8902: put the agent's own bin dir first on PATH, exactly as the session
  // wrapper does (persistent-session.ts:709). Without this the probe resolves a
  // DIFFERENT `gh` from the manager's PATH — or, as was actually the case, none
  // at all.
  //
  // This is the same principle as the env overlay above, applied to the other
  // half of the invocation. That comment says probes should see "the exact path
  // the agent's tool calls take"; the agent's `gh` IS `.claude/agt-bin/gh` (the
  // ENG-8344 credential shim), so a probe running against the manager's PATH was
  // never probing what the agent uses.
  //
  // The cost of not doing this was a fleet-wide false `down`: every GitHub
  // integration on the host reported a climbing failure count while GitHub
  // worked. Existence-gated like the session wrapper, so this is inert on any
  // agent without the directory.
  try {
    const agentBinDir = join(projectDir, '.claude', 'agt-bin');
    if (existsSync(agentBinDir)) {
      // `path.delimiter`, not a hardcoded ':' — the value is what the platform
      // parses, and this file has no other reason to assume POSIX. (The session
      // wrapper next door genuinely is POSIX-only: it writes bash. This is not,
      // so it should not inherit that assumption by accident. CodeRabbit.)
      //
      // ENG-9005 — THE SHIM MUST NEVER BE THE ONLY THING ON PATH.
      //
      // The previous fallback was `: agentBinDir`, which on a manager process
      // with no inherited PATH produced a PATH containing the shim directory
      // and NOTHING ELSE. That is not a hypothetical shape: it is exactly the
      // reported symptom, because the ENG-8344 `gh` shim resolves the real
      // binary by walking PATH for a `gh` that is not itself
      // (`findRealGh()`, github-broker-credentials.ts:415). Given only its own
      // directory it finds itself, refuses, and reports
      //
      //   "the real gh binary is not on PATH (only the Augmented shim is)"
      //
      // which is precisely what agt-aws-1 returns today while `/usr/bin/gh`
      // sits there mode 755 and authenticated. The shim was found; the thing
      // behind it was made unreachable by the fallback meant to help it.
      //
      // So the dirs are ENSURED rather than merely defaulted, which covers both
      // ways of arriving at the same broken state: an absent PATH, and an
      // inherited PATH that simply lacks the system dirs. I could not determine
      // from inside a container which of the two the manager actually has, and
      // the invariant is the same either way — a probe that cannot see
      // /usr/bin cannot probe anything.
      //
      // APPENDED, never prepended: the agent's shim and the inherited PATH keep
      // precedence, so this cannot change which binary wins for any host that
      // was already resolving one correctly. It only adds a floor.
      //
      // ENG-9284 FOLLOW-UP — THE FLOOR ALONE WAS NEVER ENOUGH, AND THE PARAGRAPH
      // ABOVE IS WHY IT TOOK ELEVEN DAYS TO SEE THAT.
      //
      // "It only adds a floor" was true and was the problem. The six dirs above
      // are the ones host-bootstrap writes into /etc/environment, and on this
      // fleet NONE of them contains `gh`: `[toolkit-install]` installs
      // integration binaries through Homebrew, so the only real binary lives at
      // /home/linuxbrew/.linuxbrew/bin/gh (measured on agt-aws-1, ENG-9284).
      //
      // So this function — documented since ENG-6441 as the single definition of
      // "how a probe runs on this host" — could not find the binary it exists to
      // run. Both lanes passed anyway, each rescued by something outside this
      // file: the manager prepends the Homebrew dir to its own process env
      // before calling us (manager-worker.ts:4680), and the forced SSM lane had
      // the same dirs bolted onto its shell command (admin-debug.ts, #5387).
      // Two callers, two external fixes, one function that was wrong in both.
      // A third caller would have been wrong with no rescue at all.
      //
      // THIS CHANGES PRECEDENCE, DELIBERATELY, AND THAT IS THE POINT.
      //
      // The toolkit dirs go at position 1 — directly behind the agent's shim,
      // ahead of the inherited PATH — because that is the order the AGENT has.
      // The manager puts Homebrew ahead of everything, so on a host carrying a
      // binary in both Homebrew and /usr/local/bin (the normal case once brew
      // has installed over a distro copy) the agent runs the Homebrew one. A
      // probe resolving the other would be measuring a binary the agent never
      // calls — the same defect as ENG-9284, one layer down and much harder to
      // see, because it would return a confident verdict rather than an honest
      // `unverified`.
      //
      // The shim STAYS at position 0. It is the credential path (ENG-8344); a
      // probe that jumps over it runs unauthenticated and reports a false
      // failure. So this inserts at 1, never at 0.
      //
      // Existence-gated, so a host without Homebrew is completely unchanged, and
      // deduped below, so the scheduled lane (whose inherited PATH already has
      // the dir, courtesy of the manager) is a no-op.
      //
      // The list is not invented — it is the PATH `host-bootstrap.ts` writes
      // into /etc/environment for these hosts. POSIX-gated because these dirs
      // are meaningless on win32, and this file deliberately avoids assuming
      // POSIX elsewhere.
      const parts = probeEnv.PATH ? probeEnv.PATH.split(pathDelimiter) : [];
      parts.unshift(agentBinDir);
      if (process.platform !== 'win32') {
        // Position 1: behind the shim, ahead of the inherited PATH. `splice` at a
        // running index rather than a fixed one so the listed order survives —
        // inserting each at 1 would reverse them, and Homebrew-Linux must stay
        // ahead of Homebrew-macOS on a host that somehow has both.
        let insertAt = 1;
        for (const dir of toolkitPathDirs()) {
          if (!existsSync(dir)) continue;
          parts.splice(insertAt, 0, dir);
          insertAt += 1;
        }
        for (const dir of SYSTEM_PATH_FLOOR) {
          if (!parts.includes(dir)) parts.push(dir);
        }
      }
      // Deduplicated over the WHOLE list, not just the floor (CodeRabbit, #5126).
      //
      // My first version guarded only the dirs it appended, which left two real
      // duplicates: the `unshift` above re-adds the shim whenever the inherited
      // PATH already contains it — and an agent's own shell PATH does start with
      // that directory, so this is the normal case rather than a corner — and
      // any duplicates already present in the inherited value survived untouched.
      //
      // `Set` keeps first-occurrence order, so the shim stays at position 0 and
      // the inherited ordering behind it is unchanged. Filtered before the Set so
      // an empty segment cannot be preserved as the "first occurrence" of ''.
      probeEnv.PATH = [...new Set(parts.filter((p) => p.length > 0))].join(pathDelimiter);
    }
  } catch {
    /* same contract as above: a probe must never break on a filesystem fault */
  }

  return probeEnv;
}

/**
 * Assemble the four probe legs against an agent's wired config: a CLI `--version`
 * probe, an MCP streamable-HTTP `tools/list` handshake, a Composio connected-
 * account binding check, and a live read-only tool-call. All read the agent's OWN
 * `.mcp.json` (url + headers) so a probe exercises the exact path the agent's
 * tool calls take. `probeEnv` (from {@link buildProbeEnv}) resolves the templated
 * auth in those headers.
 */
export function buildConnectivityProbeDeps(
  projectDir: string,
  probeEnv: NodeJS.ProcessEnv,
): ConnectivityProbeDeps {
  return {
    fetchImpl: fetch,
    runCli: (binary, args) => runCliProbe(binary, args, { env: probeEnv }),
    mcpProbe: async (target) => {
      // ENG-8049: a quarantined integration is gone from .mcp.json, so probe its
      // inline reconstructed entry when the executor threaded one; else read the file.
      // ENG-8345: resolve the key against what `.mcp.json` actually declares —
      // a derived key that names no declared server was the 433-count false
      // streak. Falls back to the derived key so the diagnostic below is
      // unchanged when nothing matches.
      const serverKey = target.inlineServerEntry
        ? target.serverKey
        : resolveDeclaredServerKey(projectDir, target.definitionId, target.serverKey) ?? target.serverKey;
      const lookup = target.inlineServerEntry
        ? inlineEntryLookup(resolveHttpServerEntry(target.inlineServerEntry as McpJsonServerEntry, probeEnv))
        : readMcpHttpServerConfig(projectDir, serverKey, probeEnv);
      // ENG-8363: the cause decides the verdict, via the SAME helper the stdio
      // leg uses. This branch used to hard-code `transient_error` while its stdio
      // twin hard-coded `down` for identical input.
      if (!lookup.ok) {
        return verdictForUnavailableMcpConfig(lookup.cause, {
          serverKey,
          transport: 'http',
          source: target.inlineServerEntry ? 'inline config' : '.mcp.json',
          createdAt: target.createdAt,
          reconnectQuiescedUntil: target.reconnectQuiescedUntil,
          expectedAbsent: target.expectedAbsent || Boolean(target.inlineServerEntry),
          now: Date.now(),
        });
      }
      const cfg = lookup.value;
      if (cfg.unresolved.length > 0) {
        // ENG-6232: the auth header references env var(s) we can't substitute
        // from the agent's .env.integrations. Sending the literal `${VAR}` would
        // earn a guaranteed 401 → false 'down'; skip as non-escalating instead.
        return {
          status: 'transient_error',
          message: `MCP '${serverKey}' auth unresolved: ${cfg.unresolved.join(', ')}`,
        };
      }
      // ENG-7077: hand the prescribed read-only tool to the probe. probeMcpHttp
      // has issued a `tools/call` for a toolkit's connectivity_test since
      // ENG-6957; this lane simply never supplied one, so it could only ever
      // handshake — and a handshake cannot earn a green badge.
      return probeMcpHttp(
        target.toolName
          ? { ...cfg, connectivityTest: { tool: target.toolName, ...(target.toolArgs ? { args: target.toolArgs } : {}) } }
          : cfg,
      );
    },
    // ENG-7405: local-STDIO MCP probe. Spawns the toolkit's bundled server with
    // the agent's env (the exact command/args/env from `.mcp.json`), handshakes,
    // and calls the read-only `connectivity_test` tool when the descriptor set
    // one (threaded via target.toolName). An unresolvable `${VAR}` in the spawn
    // env → skip as non-escalating, never spawn a doomed server.
    //
    // ENG-8363: a missing server entry is no longer a flat `down`. It still is
    // one once the writer has had its chance, but the manager probes BEFORE it
    // writes `.mcp.json` in the same poll, so the first probe of every
    // newly-added integration necessarily finds the key absent — that window
    // reads `unverified` since ENG-9034 (and still keeps ENG-7575's first-connect
    // backoff engaged, which now accepts `unverified` inside the window).
    // `verdictForUnavailableMcpConfig` owns the distinction.
    mcpStdioProbe: async (target) => {
      // ENG-8049: prefer the inline reconstructed entry for a quarantined row.
      // ENG-8345: same resolve-don't-derive rule as the HTTP leg. Since
      // ENG-8363 both legs escalate an unmatched key to `down` once the row is
      // past the first-connect window, so the rule matters equally on each —
      // this leg is no longer the only one that can hard-fail on a bad key.
      const serverKey = target.inlineServerEntry
        ? target.serverKey
        : resolveDeclaredServerKey(projectDir, target.definitionId, target.serverKey) ?? target.serverKey;
      const lookup = target.inlineServerEntry
        ? inlineEntryLookup(resolveStdioServerEntry(target.inlineServerEntry as McpJsonServerEntry, probeEnv))
        : readMcpStdioServerConfig(projectDir, serverKey, probeEnv);
      if (!lookup.ok) {
        return verdictForUnavailableMcpConfig(lookup.cause, {
          serverKey,
          transport: 'stdio',
          source: target.inlineServerEntry ? 'inline config' : '.mcp.json',
          createdAt: target.createdAt,
          reconnectQuiescedUntil: target.reconnectQuiescedUntil,
          expectedAbsent: target.expectedAbsent || Boolean(target.inlineServerEntry),
          now: Date.now(),
        });
      }
      const cfg = lookup.value;
      if (cfg.unresolved.length > 0) {
        return {
          status: 'transient_error',
          message: `MCP stdio '${serverKey}' env unresolved: ${cfg.unresolved.join(', ')}`,
        };
      }
      return probeMcpStdio({
        command: cfg.command,
        args: cfg.args,
        env: cfg.env,
        cwd: projectDir,
        connectivityTest: target.toolName ? { tool: target.toolName, args: target.toolArgs ?? null } : null,
      });
    },
    // ENG-6139: connected-account binding check for managed (Composio) toolkits.
    // The MCP handshake reads green on a dead/mis-bound account, so the managed
    // probe also verifies the account is ACTIVE + bound to the entity the agent
    // queries with. Inputs come from the agent's OWN wired MCP server: the
    // `x-api-key` header and the `user_id` query param (the agent already
    // authenticates with these), plus the recorded connected_account_id.
    composioProbe: async (serverKey, credentials, inlineServerEntry, rowCtx) => {
      // ENG-7543: the executor passes the already-resolved (connection-suffixed)
      // server key so the binding check reads the correct connection's server —
      // no toolkit-level re-derivation here.
      // ENG-8049: for a quarantined row, resolve the inline reconstructed entry.
      const lookup = inlineServerEntry
        ? inlineEntryLookup(resolveHttpServerEntry(inlineServerEntry as McpJsonServerEntry, probeEnv))
        : readMcpHttpServerConfig(projectDir, serverKey, probeEnv);
      // ENG-8363: this leg is a THIRD site that had its own idea of what an
      // unreadable config means (it hard-coded `transient_error`). Routed through
      // the shared helper so all three agree by construction.
      if (!lookup.ok) {
        return verdictForUnavailableMcpConfig(lookup.cause, {
          serverKey,
          transport: 'http',
          source: inlineServerEntry ? 'inline config' : '.mcp.json',
          createdAt: rowCtx?.createdAt,
          reconnectQuiescedUntil: rowCtx?.reconnectQuiescedUntil,
          expectedAbsent: rowCtx?.expectedAbsent || Boolean(inlineServerEntry),
          now: Date.now(),
        });
      }
      const cfg = lookup.value;
      if (cfg.unresolved.length > 0) {
        // ENG-6232: unresolved templated auth (e.g. x-api-key) → skip, never a false 'down'.
        return {
          status: 'transient_error',
          message: `MCP '${serverKey}' auth unresolved: ${cfg.unresolved.join(', ')}`,
        };
      }
      // HTTP header names are case-insensitive — match `x-api-key` regardless of
      // the casing the .mcp.json writer used (e.g. `X-Api-Key`), not just two
      // hardcoded variants, so a probe can't false-fail on a casing mismatch.
      const apiKey =
        Object.entries(cfg.headers ?? {}).find(([k]) => k.toLowerCase() === 'x-api-key')?.[1] ?? '';
      let expectedUserId = '';
      // ENG-6157: the wired serverId is embedded in the MCP URL path
      // (`/v3/mcp/<serverId>/mcp`). Passing it lets the probe assert the
      // account's auth_config is one this exact server resolves with — the
      // server the agent ACTUALLY queries, the ground-truth source.
      let serverId: string | undefined;
      try {
        const u = new URL(cfg.url);
        expectedUserId = u.searchParams.get('user_id') ?? '';
        const m = u.pathname.match(/\/v3\/mcp\/([^/]+)\/mcp/);
        serverId = m?.[1] ? decodeURIComponent(m[1]) : undefined;
      } catch {
        expectedUserId = '';
      }
      const connectedAccountId =
        typeof credentials?.['connected_account_id'] === 'string'
          ? (credentials['connected_account_id'] as string)
          : '';
      return probeComposioAccount({ connectedAccountId, apiKey, expectedUserId, serverId });
    },
    // ENG-6157 (Phase 2): the live tool-call leg. Uses the agent's OWN wired MCP
    // URL + headers (the exact path tool calls take), so a broken auth_config
    // linkage surfaces as a real `No connected account found` instead of a
    // green handshake. Skips (`null`) when no safe read-only tool is callable.
    composioToolCallProbe: async (target) => {
      // ENG-8049: prefer the inline reconstructed entry for a quarantined row.
      const lookup = target.inlineServerEntry
        ? inlineEntryLookup(resolveHttpServerEntry(target.inlineServerEntry as McpJsonServerEntry, probeEnv))
        : readMcpHttpServerConfig(projectDir, target.serverKey, probeEnv);
      // ENG-8363: deliberately still a SKIP, not a verdict — unlike the three
      // legs above. This leg only ever ADDS signal (`managed_composite` folds
      // worst-wins, and the mcpProbe leg has already produced the config verdict
      // for the same server key). Returning a verdict here would double-count the
      // identical fact; returning null leaves the fold untouched.
      if (!lookup.ok) return null;
      const cfg = lookup.value;
      // ENG-6232: can't substitute the auth header → skip (null = not reported),
      // never fire a doomed tool call that would read as a false signal.
      if (cfg.unresolved.length > 0) return null;
      // ENG-6242: call the toolkit's prescribed `connectivity_test.tool` (when set)
      // — the SAME read-only tool the central Test path uses — instead of
      // auto-picking. probeComposioMcpToolCall re-validates it read-only against the
      // live tools/list and falls back to the heuristic on drift, so a stale/missing
      // override can never run an unsafe tool. Fixes the false-RED that auto-pick
      // produced for managed Linear (LINEAR_GET_CURRENT_USER).
      return probeComposioMcpToolCall({
        url: cfg.url,
        headers: cfg.headers,
        toolName: target.toolName,
        toolArgs: target.toolArgs,
      });
    },
  };
}
